import { FoundNodeInfo } from '@common/prosemirror/model/node-find';
import { nodeInterior } from '@common/prosemirror/model/range';
import { resolvedPosFind } from '@common/prosemirror/model/resolved-pos';
import { Node, NodeRange, NodeType, ProseMirrorNode, ResolvedPos } from 'prosemirror-model';
import { Transform, canJoin, liftTarget } from 'prosemirror-transform';

// Pulled from pm code
function canCut(node: Node, start: number, end: number): boolean {
  return (start === 0 || node.canReplace(start, node.childCount)) &&
    (end === node.childCount || node.canReplace(0, end));
}

// Returns the depth of the nearest node that matches nodeType that the range can be lifted to
export function liftTargetFromNodeType(range: NodeRange, nodeType: NodeType): number {
  const content = range.parent.content.cutByIndex(range.startIndex, range.endIndex);

  const fromNodeInfo = resolvedPosFind(range.$from, node => node.type === nodeType);

  if (fromNodeInfo) {
    for (let depth = fromNodeInfo.depth - 1; depth >= 0; --depth) {
      const node = range.$from.node(depth),
        index = range.$from.index(depth),
        endIndex = range.$to.indexAfter(depth);

      if (depth < range.depth && node.canReplace(index, endIndex, content)) {
        return depth;
      }

      if (depth === 0 || node.type.spec.isolating || !canCut(node, index, endIndex)) {
        break;
      }
    }
  }
}

// Sets the attributes of a node by adding/setting the given attrs to the node's existing attributes
export function updateNodeAttrs(tr: Transform, node: Node, pos: number, attrs: object): Transform {
  return tr.setNodeMarkup(pos, null, Object.assign({}, node.attrs, attrs));
}

/**
 * Unbinds the node from the document, also it auto joins nodes on bounds of the the unbind node.
 * @param isJoinable - function that returns true if the left and right nodes can be joined after unbinding, it calls only for nodes when their join is possible in general.
 */
export function unbind(tr: Transform, node: ProseMirrorNode, pos: number, isJoinable?: (left: FoundNodeInfo, right: FoundNodeInfo) => boolean): Transform {
  const range = nodeInterior(tr.doc, node, pos);
  const target = liftTarget(range);
  if (!target)
    throw new RangeError('Unable to unbind the node at the specified position');
  tr.lift(range, target);
  tryJoin(tr, tr.mapping.map(pos), isJoinable);
  tryJoin(tr, tr.mapping.map(pos + node.nodeSize), isJoinable);
  return tr;
}

/**
 * Tries to join adjusted nodes at the specified position.
 * @param isJoinable - function that returns true if the left and right nodes can be joined, it calls only for nodes when their join is possible in general e.g. they have same markup.
 */
export function tryJoin(tr: Transform, pos: number, isJoinable?: (left: FoundNodeInfo, right: FoundNodeInfo) => boolean): Transform {
  if (canJoin(tr.doc, pos)) /* ask prosemirror to add depth argument, like in canSplit */ {
    const depth = joinDepth(tr.doc, pos, isJoinable);
    if (depth) tr.join(pos, depth);
  }
  return tr;
}

/**
 * Gets the possible depth for join adjusted nodes at specified position.
 */
function joinDepth(doc: ProseMirrorNode, pos: number, isJoinable?: (left: FoundNodeInfo, right: FoundNodeInfo) => boolean): number {
  const getDepth = ($left: ResolvedPos, $right: ResolvedPos): number => {
    const left = $left.nodeBefore, right = $right.nodeAfter;
    if (left && right && !left.isLeaf && left.canAppend(right) && left.sameMarkup(right) &&
      (!isJoinable || isJoinable({ node: left, pos: $left.pos }, { node: right, pos: $right.pos }))) {
      const $prev = doc.resolve($left.pos - 1);
      const $next = doc.resolve($right.pos + 1);
      return getDepth($prev, $next) + 1;
    }
    return 0;
  }

  const $pos = doc.resolve(pos);
  return getDepth($pos, $pos);
}
